import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { GameMode } from '@sorare/core/src/__generated__/globalTypes';
import Button from '@sorare/core/src/atoms/buttons/Button';
import { Locker } from '@sorare/core/src/atoms/icons/Locker';
import { FOOTBALL_PLAY_RIVALS_SQUADS } from '@sorare/core/src/constants/__generated__/routes';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';
import { glossary } from '@sorare/core/src/lib/glossary';

import { CreateSquadDialog } from 'components/rivals/squads/CreateSquadDialog';
import { InviteDialog } from 'components/rivals/squads/InviteDialog';

import {
  CreateSquadMutation,
  CreateSquadMutationVariables,
} from './__generated__/index.graphql';

const CREATE_SQUAD_MUTATION = gql`
  mutation CreateSquadMutation($input: createSo5UserGroupInput!) {
    createSo5UserGroup(input: $input) {
      userGroup {
        slug
        ...InviteDialog_userGroupInterface
      }
      errors {
        path
        message
        code
      }
    }
  }
  ${InviteDialog.fragments.userGroupInterface}
` as TypedDocumentNode<CreateSquadMutation, CreateSquadMutationVariables>;

type Props = { creationLocked: boolean };
export const CreateSquadButton = ({ creationLocked }: Props) => {
  const [userGroup, setUserGroup] =
    useState<
      NonNullable<CreateSquadMutation['createSo5UserGroup']>['userGroup']
    >();
  const [create, { loading }] = useMutation(CREATE_SQUAD_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const [step, setStep] = useState<'noDialog' | 'create' | 'invite'>(
    'noDialog'
  );
  const navigate = useNavigate();

  const onNext = async ({
    displayName,
    logoId,
  }: {
    displayName: string;
    logoId: string;
  }) => {
    try {
      const { data } = await create({
        variables: {
          input: {
            displayName,
            gameMode: GameMode.RIVALS_SQUAD,
            logoId,
          },
        },
      });
      const fetchedUserGroup = data?.createSo5UserGroup?.userGroup;

      if (fetchedUserGroup) {
        setUserGroup(fetchedUserGroup);
        setStep('invite');
      }
    } catch {
      setStep('noDialog');
    }
  };

  return (
    <>
      <Button
        color={creationLocked ? 'tertiary' : 'primary'}
        size="large"
        disabled={creationLocked}
        onClick={() => setStep('create')}
      >
        {creationLocked && <Locker width={16} height={16} />}
        <FormattedMessage {...glossary.create} />
      </Button>
      {step === 'create' && (
        <CreateSquadDialog
          open
          onClose={() => setStep('noDialog')}
          onNext={onNext}
          loading={loading}
        />
      )}
      {step === 'invite' && !!userGroup && (
        <InviteDialog
          open
          userGroup={userGroup}
          onClose={() => {
            navigate(FOOTBALL_PLAY_RIVALS_SQUADS, { replace: true });
          }}
        />
      )}
    </>
  );
};
